import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Dialog, Snackbar } from "@mui/material";
import { baseURL } from "../config/api";
import colorConfigs from "../config/colorConfigs";
import saveAs from "file-saver";

type FileItem = {
    fileName: string; // URL or file name string
    id?: number;
};

type Props = {
    handlePopup: () => void;
    showPopup: boolean;
    filePath?: FileItem[] | FileItem | string | null;
    fileIndex?: number;
    setFileIndex?: (value: number) => void;
    imageBaseUrlPath: string;
    updateFile?: (e: React.ChangeEvent<HTMLInputElement>, id: number) => void;
    onIndexChange?: (idx: number) => void;
    isSupportTicket?: boolean;
};

export const CustomImagePopup: React.FC<Props> = ({
    showPopup,
    handlePopup,
    filePath,
    fileIndex: externalIndex,
    setFileIndex: externalSetFileIndex,
    imageBaseUrlPath,
    updateFile,
    onIndexChange,
    isSupportTicket = false
}) => {
    const [showCopyPopup, setShowCopyPopup] = useState<boolean>(false);
    const [isFullScreen, setIsFullScreen] = useState<boolean>(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [localIndex, setLocalIndex] = useState<number>(0);

    const items: FileItem[] = useMemo(() => {
        if (!filePath) return [];
        if (Array.isArray(filePath)) {
            return filePath.map((it) => (typeof it === "string" ? { fileName: it } : (it as FileItem)));
        }
        return typeof filePath === "string" ? [{ fileName: filePath }] : [filePath as FileItem];
    }, [filePath]);

    const currentIndex = typeof externalIndex === "number" ? externalIndex : localIndex;

    const setIndex = (value: number) => {
        const clamped = Math.max(0, Math.min(value, Math.max(0, items.length - 1)));
        if (externalSetFileIndex) externalSetFileIndex(clamped);
        else setLocalIndex(clamped);
        if (onIndexChange) onIndexChange(clamped);
    };

    useEffect(() => {
        if (items?.length === 0) {
            setIndex(0);
            return;
        }
        if (currentIndex < 0 || currentIndex > items?.length - 1) {
            setIndex(Math.min(currentIndex, items?.length - 1));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items?.length]);

    useEffect(() => {
        if (!showPopup) return;
        const handler = (e: KeyboardEvent) => {
            if (e.key === "ArrowLeft") {
                if (currentIndex > 0) setIndex(currentIndex - 1);
            } else if (e.key === "ArrowRight") {
                if (currentIndex < items?.length - 1) setIndex(currentIndex + 1);
            } else if (e.key === "Escape") {
                setIsFullScreen(false);
                handlePopup();
            }
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showPopup, currentIndex, items?.length]);

    const handleUpdateClick = () => fileInputRef.current?.click();

    const getFilenameFromFileNameField = (raw: string | undefined) => {
        if (!raw) return "";
        if (raw.includes("Get?name=")) {
            try {
                return raw.split("Get?name=")[1] ?? raw;
            } catch {
                return raw;
            }
        }
        try {
            const url = new URL(raw);
            const pathname = url.pathname;
            const lastSegment = pathname.split("/").pop();
            if (lastSegment) return lastSegment;
        } catch {
            // not a URL
        }
        return raw.replace(/\?.*$/, "").split("/").pop() || raw;
    };

    const currentItem: FileItem | null = items[currentIndex] ?? null;

    const safeFileName = currentItem?.fileName ?? "";
    const downloadTheImage = () => {
        if (!safeFileName) return;

        const extractedName = getFilenameFromFileNameField(safeFileName);
        const filename = extractedName || `image-${currentIndex + 1}.png`;

        saveAs(`${baseURL}${imageBaseUrlPath}/download?name=${filename}`, `${filename}.png`);
    };

    const copyLink = async () => {
        if (!safeFileName) return;

        try {
            await navigator.clipboard.writeText(safeFileName);
            setShowCopyPopup(true);
            setTimeout(() => setShowCopyPopup(false), 1000);
        } catch {
            setShowCopyPopup(true);
            setTimeout(() => setShowCopyPopup(false), 1000);
        }
    };

    return (
        <>
            <Dialog
                open={showPopup}
                fullScreen={isFullScreen}
                sx={{
                    '& .MuiDialog-paper': {
                        maxWidth: isFullScreen ? "100%" : "75%",
                        width: '100%',
                        borderRadius: isFullScreen ? "0px" : '16px',
                        backgroundColor: "white",
                        overflow: "hidden"
                    },
                    zIndex: 9999,

                }}
            >
                <div className={`px-[10px] py-[5px] h-full bg-white`}>
                    <div className="w-full bg-transparent rounded-lg py-[10px] flex flex-col gap-2.5 h-full">
                        {/* Header */}
                        <div className={`flex justify-between items-center px-[18px] py-2 border-b border-secondary`}>
                            <h2 className={`text-lg font-semibold text-secondary`}>Preview</h2>
                            <svg
                                onClick={() => {
                                    setIsFullScreen(false);
                                    handlePopup();
                                }}
                                className="cursor-pointer"
                                style={{ color: "#2f3739", width: "20px", height: "20px" }}
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M6 6L18 18"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                />
                                <path
                                    d="M6 18L18 6"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                />
                            </svg>
                        </div>

                        <div className="flex-1 mt-2 py-2">
                            {safeFileName ? (
                                isFullScreen ? (
                                    <div className="flex justify-between items-center">
                                        <div className="w-[5%] p-2 text-center">
                                            {
                                                typeof externalIndex === "number" && (
                                                    <svg
                                                        onClick={() => currentIndex !== 0 && setIndex(currentIndex - 1)}
                                                        className="cursor-pointer"
                                                        style={{
                                                            cursor: currentIndex !== 0 ? "pointer" : "default",
                                                            opacity: currentIndex === 0 ? 0.5 : 1,
                                                            color: "gray",
                                                            width: "20px",
                                                            height: "20px",
                                                        }}
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path
                                                            d="M15 6L9 12L15 18"
                                                            stroke="currentColor"
                                                            strokeWidth="2"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                        />
                                                    </svg>
                                                )
                                            }

                                        </div>

                                        <div className={`w-[90%] ${isFullScreen ? "max-h-[calc(100vh-160px)]" : "h-full"} overflow-y-auto`}>
                                            <img
                                                src={safeFileName}
                                                alt="preview"
                                                style={{
                                                    width: "100%",
                                                    maxHeight: isFullScreen ? "100%" : "67vh",
                                                    height: isFullScreen ? "100%" : "67vh",
                                                    objectFit: "fill",
                                                }}
                                            />
                                        </div>

                                        <div className="w-[5%] p-2 text-center">
                                            {
                                                typeof externalIndex === "number" && (
                                                    <svg
                                                        onClick={() =>
                                                            currentIndex !== items.length - 1 && setIndex(currentIndex + 1)
                                                        }
                                                        className="cursor-pointer"
                                                        style={{
                                                            cursor: currentIndex !== items.length - 1 ? "pointer" : "default",
                                                            opacity: currentIndex === items.length - 1 ? 0.5 : 1,
                                                            color: "gray",
                                                            width: "20px",
                                                            height: "20px",
                                                        }}
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path
                                                            d="M9 6L15 12L9 18"
                                                            stroke="currentColor"
                                                            strokeWidth="2"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                        />
                                                    </svg>
                                                )
                                            }

                                        </div>
                                    </div>
                                ) : (
                                    // ✅ Non-fullscreen layout
                                    <div className="flex justify-between items-center">
                                        <div className="w-[5%] p-2 text-center">
                                            {
                                                typeof externalIndex === "number" && (
                                                    <svg
                                                        onClick={() => currentIndex !== 0 && setIndex(currentIndex - 1)}
                                                        className="cursor-pointer"
                                                        style={{
                                                            cursor: currentIndex !== 0 ? "pointer" : "default",
                                                            opacity: currentIndex === 0 ? 0.5 : 1,
                                                            color: "gray",
                                                            width: "20px",
                                                            height: "20px",
                                                        }}
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path
                                                            d="M15 6L9 12L15 18"
                                                            stroke="currentColor"
                                                            strokeWidth="2"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                        />
                                                    </svg>
                                                )
                                            }
                                        </div>

                                        <div className={`w-[90%] ${isFullScreen ? "max-h-[calc(100vh-160px)]" : "h-full"} overflow-y-auto`}>
                                            {/* Image */}
                                            <img
                                                src={safeFileName}
                                                alt="preview"
                                                style={{
                                                    width: "100%",
                                                    maxHeight: isFullScreen ? "100%" : "67vh",
                                                    height: isFullScreen ? "100%" : "67hvh",
                                                    objectFit: "fill",
                                                }}
                                            />
                                        </div>

                                        <div className="w-[5%] p-2 text-center">
                                            {
                                                typeof externalIndex === "number" && (
                                                    <svg
                                                        onClick={() =>
                                                            currentIndex !== items.length - 1 && setIndex(currentIndex + 1)
                                                        }
                                                        className="cursor-pointer"
                                                        style={{
                                                            cursor: currentIndex !== items.length - 1 ? "pointer" : "default",
                                                            opacity: currentIndex === items.length - 1 ? 0.5 : 1,
                                                            color: "gray",
                                                            width: "20px",
                                                            height: "20px",
                                                        }}
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path
                                                            d="M9 6L15 12L9 18"
                                                            stroke="currentColor"
                                                            strokeWidth="2"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                        />
                                                    </svg>
                                                )
                                            }

                                        </div>
                                    </div>
                                )
                            ) : (
                                // No image fallback
                                <div className="w-full h-[300px] flex items-center justify-center border rounded">
                                    <p className="text-secondary">No image available</p>
                                </div>
                            )}
                        </div>

                        {/* Footer Buttons */}
                        <div className="w-full flex justify-between items-center mt-2 px-[10px]">
                            <div className="flex-1">
                                <Button
                                    variant="contained"
                                    sx={{
                                        backgroundColor: colorConfigs.btn.cancel.bg,
                                        border: colorConfigs.btn.cancel.border,
                                        color: colorConfigs.btn.cancel.color,
                                        fontSize: "10px",
                                    }}
                                    onClick={() => setIsFullScreen(!isFullScreen)}
                                    disabled={!safeFileName}
                                >
                                    {isFullScreen ? "Exit " : ""} Full Screen
                                </Button>
                            </div>

                            <div className="flex-1">
                                {/* Image Index */}
                                {items?.length > 1 && (
                                    <div className="w-full text-center text-xs text-gray-400">
                                        {Math.min(currentIndex + 1, items?.length)} of {items?.length}
                                    </div>
                                )}
                            </div>

                            <div className="flex gap-1.5 flex-1 justify-end">
                                {
                                    (!safeFileName.includes("blob:")) && (
                                        <Button
                                            variant="contained"
                                            sx={{
                                                backgroundColor: colorConfigs.btn.cancel.bg,
                                                border: colorConfigs.btn.cancel.border,
                                                color: colorConfigs.btn.cancel.color,
                                                fontSize: "10px",
                                            }}
                                            onClick={copyLink}
                                            disabled={!safeFileName}
                                        >
                                            Copy Link
                                        </Button>
                                    )
                                }


                                {(externalIndex ? (typeof currentItem?.id === "number" && currentItem.id > 0 && updateFile) : updateFile) && (
                                    <Button
                                        variant="contained"
                                        sx={{ backgroundColor: "#DE6F0D", fontSize: "10px" }}
                                        onClick={handleUpdateClick}
                                    >
                                        Update New Image
                                    </Button>
                                )}
                                {
                                    (!safeFileName.includes("blob:")) && (
                                        <Button
                                            variant="contained"
                                            sx={{ backgroundColor: "#039855", color: "#D9F4D4", fontSize: "10px" }}
                                            onClick={downloadTheImage}
                                            disabled={!safeFileName}
                                        >
                                            Download
                                        </Button>
                                    )
                                }
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>

            <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={(e) => {
                    if (!updateFile) return;
                    // pass id if present, otherwise pass -1 as fallback (change to your preferred fallback)
                    updateFile(e, currentItem?.id ?? -1);
                }}
            />

            <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={showCopyPopup} autoHideDuration={1000}>
                <Alert severity="success" variant="filled" sx={{ width: '100%' }}>
                    Link Copied.
                </Alert>
            </Snackbar>
        </>
    );
};